/*
Example usage:

1) Showing a notification on render:

  useNotifications('Notification text', 'normal', true);

2) Triggering a notification:

  const { showNotification } = useNotifications('Notification text');
  ...
  showNotification()

3) Updating the default notification:

  const { showNotification } = useNotifications('Warning notification text', 'warning');
  ...
  showNotification('Error notification text', 'error')

4) Triggering a notification with (collapsible) details:

  const { showNotification } = useNotifications('Error notification text', 'error', true, 5000, 'This is the long text that will appear in the collapsible section in the box.');
  ...
  showNotification()

    OR

  const { showNotification } = useNotifications();
  ...
  showNotification('Error notification text', 'error', 5000, 'This is the long text that will appear in the collapsible section in the box.')

5) Hiding the last notification (e.g. with collapsible details, when no close button available):

  const { closeNotification } = useNotifications();
  ...
  closeNotification()
*/

import { NotificationVariation } from '@salecycle/ui-component-library';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import {
  _showNotification,
  useNotificationsState,
  useNotificationsDispatch,
  _closeNotification,
} from 'core/notifications/useNotificationsContext';
import usePrevious from 'core/ui/utils/usePrevious';

export function useNotifications(
  initialText = '',
  variation: NotificationVariation = 'normal',
  initialVisibilty = false,
  initialDelay = 5000,
  // initialDetailsText is at the 5th position in param list because of backward compatibility
  initialDetailsText = '',
) {
  const { visible: currentNotificationVisibility, text: currentNotificationText } = useNotificationsState();
  const dispatch = useNotificationsDispatch();

  const [notificationDelay, setNotificationDelay] = useState(initialDelay);
  const [notificationText, setNotificationText] = useState(initialText);
  const [notificationDetailsText, setNotificationDetailsText] = useState<string | ReactNode>(initialDetailsText);
  const [notificationVisible, setNotificationVisible] = useState(initialVisibilty);

  const prevNotificationText = usePrevious(currentNotificationText);

  // Update notification visibility
  useEffect(() => {
    if (currentNotificationText === notificationText) {
      // Update the visiblity if the current notification content updates
      setNotificationVisible(currentNotificationVisibility);
    } else if (currentNotificationVisibility && notificationText === prevNotificationText) {
      // Set internal visiblity to false if another notification is shown
      setNotificationVisible(false);
    }
  }, [currentNotificationVisibility, currentNotificationText, notificationText, prevNotificationText]);

  // Show the notification
  const show = useCallback(
    (
      text: string = notificationText,
      notificationVariation: NotificationVariation = variation,
      delay: number = notificationDelay,
      // detailsText is at the 4th position in param list because of backward compatibility
      detailsText: string | ReactNode = '',
    ) => {
      // Immediately show the notification as long as it has a text value
      if (text !== '') {
        _showNotification(dispatch, text, detailsText, notificationVariation, delay);
      }

      // Store notification values in state
      setNotificationDelay(delay);
      setNotificationText(text);
      setNotificationDetailsText(detailsText);
      setNotificationVisible(true);
    },
    [dispatch, notificationDelay, notificationText, variation],
  );

  // Hide the notification
  const hide = useCallback(() => {
    _closeNotification(dispatch);
    setNotificationVisible(false);
  }, [dispatch]);

  return {
    values: { notificationDelay, notificationText, notificationDetailsText, variation, notificationVisible },
    showNotification: show,
    closeNotification: hide,
  };
}
export default useNotifications;
